/**
 * Dialog for to-punkts skala-kalibrering av plantegningen.
 * Vises når begge kalibreringspunktene er satt i 2D-canvaset.
 *
 * Regnemodell: punktene klikkes i world-koordinater mens bildet er strukket
 * over dagens gulvflate. Faktor = oppgitt avstand / målt world-avstand.
 * Lager-dimensjonene skaleres proporsjonalt med faktoren (rundet til 0,5 m)
 * — dimensjonene ER kalibreringen siden bildet alltid strekkes til gulvet.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Ruler, X } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';

const MIN_DIMENSION_M = 5;
const MAX_DIMENSION_M = 500;

const roundToHalf = (value: number) => Math.round(value * 2) / 2;

const CalibrationDialog: React.FC = () => {
  const { state, dispatch } = useLayoutEditor();
  const { t } = useTranslation();
  const [distanceInput, setDistanceInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { calibration, layout } = state;
  const isOpen = calibration.active && !!calibration.pointA && !!calibration.pointB;

  // Nullstill og fokuser input hver gang dialogen åpnes
  useEffect(() => {
    if (isOpen) {
      setDistanceInput('');
      // Etter render slik at elementet finnes
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const measuredMeters = useMemo(() => {
    if (!calibration.pointA || !calibration.pointB) return 0;
    return Math.hypot(
      calibration.pointB.x - calibration.pointA.x,
      calibration.pointB.y - calibration.pointA.y
    );
  }, [calibration.pointA, calibration.pointB]);

  if (!isOpen || !layout) return null;

  const parsedDistance = parseFloat(distanceInput.replace(',', '.'));
  const factor = measuredMeters > 0 && parsedDistance > 0 ? parsedDistance / measuredMeters : 0;

  const newLength = factor > 0 ? roundToHalf(layout.dimensions.length * factor) : 0;
  const newWidth = factor > 0 ? roundToHalf(layout.dimensions.width * factor) : 0;

  const withinBounds =
    newLength >= MIN_DIMENSION_M && newLength <= MAX_DIMENSION_M &&
    newWidth >= MIN_DIMENSION_M && newWidth <= MAX_DIMENSION_M;
  const canApply = factor > 0 && withinBounds;

  const handleApply = () => {
    if (!canApply) return;
    dispatch({ type: 'APPLY_CALIBRATION', length: newLength, width: newWidth });
  };

  const handleCancel = () => dispatch({ type: 'CANCEL_CALIBRATION' });

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40">
      <div className="w-80 bg-white rounded-xl shadow-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <Ruler className="w-4 h-4 text-amber-500" />
            {t('manager.modules.warehouseLayout.calibration.dialogTitle', {}, 'Kalibrer målestokk')}
          </h3>
          <button onClick={handleCancel} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="block text-xs text-gray-600 mb-1">
          {t('manager.modules.warehouseLayout.calibration.distanceLabel', {}, 'Hvor lang er avstanden A–B i virkeligheten? (meter)')}
        </label>
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={distanceInput}
          onChange={(e) => setDistanceInput(e.target.value.replace(/[^0-9.,]/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canApply) handleApply();
            if (e.key === 'Escape') handleCancel();
          }}
          placeholder={t('manager.modules.warehouseLayout.calibration.distancePlaceholder', {}, 'f.eks. 42')}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-400"
        />

        {factor > 0 && (
          <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600 space-y-0.5">
            <div>
              {t('manager.modules.warehouseLayout.calibration.previewCurrent', {}, 'Nå')}
              {': '}{layout.dimensions.length}m × {layout.dimensions.width}m
            </div>
            <div className="font-medium text-gray-800">
              {t('manager.modules.warehouseLayout.calibration.previewNew', {}, 'Etter kalibrering')}
              {': '}{newLength}m × {newWidth}m
            </div>
            {!withinBounds && (
              <div className="text-red-600">
                {t('manager.modules.warehouseLayout.calibration.outOfBounds', {}, 'Resultatet er utenfor gyldig område (5–500 m) — sjekk avstanden')}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleCancel}
            className="flex-1 px-3 py-1.5 text-xs text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
          >
            {t('manager.modules.warehouseLayout.calibration.cancel', {}, 'Avbryt')}
          </button>
          <button
            onClick={handleApply}
            disabled={!canApply}
            className="flex-1 px-3 py-1.5 text-xs text-white bg-amber-500 rounded hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('manager.modules.warehouseLayout.calibration.apply', {}, 'Bruk målestokk')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalibrationDialog;
